import type { ReactNode } from "react";
import { useParams } from "react-router";
import { ChatMarkdown } from "@/components/chat/ChatMarkdown";
import { findLegalDocument } from "@/policies";
import { BackLink, ClientsPageFrame } from "@/v2/clients/ClientsLayout";
import { LEGAL_PATH } from "@/v2/clients/paths";

/** A policy page under the client pages' header, led back to the Legal page. */
function PolicyFrame({ children }: { children: ReactNode }) {
  return (
    <ClientsPageFrame crumbs={[]}>
      <div className="mx-auto max-w-3xl px-6 pt-6">
        <BackLink to={LEGAL_PATH}>All legal documents</BackLink>
      </div>
      {children}
    </ClientsPageFrame>
  );
}

/**
 * One legal document at a stable, shareable URL.
 *
 * Public. The consent dialog links here, and the gate blocks everything behind
 * it, so a signed-in user who has not accepted yet still has to be able to open
 * the full text. It also means a policy can be linked from an email or the
 * login page without inventing a second surface for the same words.
 */
export function PolicyPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const policy = findLegalDocument(policyId ?? "");

  if (!policy) {
    return (
      <PolicyFrame>
        <section className="mx-auto max-w-3xl px-6 py-10">
          <h1 className="text-xl font-semibold">Document not found</h1>
          <p className="mt-2 text-muted-foreground">
            There is no legal document at this address.
          </p>
        </section>
      </PolicyFrame>
    );
  }

  const { document } = policy;

  return (
    <PolicyFrame>
      <article className="mx-auto max-w-3xl px-6 py-6">
        <h1 className="text-2xl font-semibold">{document.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Version {document.version}, effective {document.effectiveDate}
          {document.status === "draft"
            ? " — draft, pending legal approval"
            : ""}
        </p>
        <div className="mt-8">
          <ChatMarkdown content={document.body} />
        </div>
      </article>
    </PolicyFrame>
  );
}
